/**
 * Task row renderer for the GraphQL-backed Tasks Report table.
 *
 * Key differences from the legacy TaskRow:
 * - The task description comes as ADF JSON (body.adf) instead of pre-rendered HTML.
 *   Rendered via @atlaskit/renderer ReactRenderer for full fidelity.
 * - Dates arrive as ISO strings (dueAt, createdAt, completedAt) from the AGG.
 * - Assignee / creator come as { accountId, displayName, avatarPath }.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { Checkbox } from '@atlaskit/checkbox/checkbox';
import { cssMap } from '@atlaskit/css';
import ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import ReactRenderer from '@atlaskit/renderer/renderer';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { createMentionsConfig } from '@confluence/fabric-media-support/entry-points/MentionsProvider.ts';
import { useProfilecardProvider } from '@confluence/fabric-providers/entry-points/useProfileCardProvider.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { TaskEntity } from '../hooks/useTasksReportData.ts';
import type { TasksReportColumn } from '../hooks/useParsedMacroParams.ts';
import { useCompleteTask, type CompleteTaskTrigger } from '../hooks/useCompleteTask.ts';
import { buildTaskAssigneeProfileUrl, normalizeTaskUrl } from '../tasksReportUrlUtils.ts';
import { i18n } from '../tasksReportMacroI18n.ts';

interface TaskRowProps {
	task: TaskEntity;
	columns: TasksReportColumn[];
	taskUpdateTrigger?: CompleteTaskTrigger;
	/** Optional callback fired when task is successfully marked complete. */
	onTaskComplete?: (taskId: string) => Promise<void> | void;
}

interface TaskDescriptionCellProps {
	task: TaskEntity;
	taskUpdateTrigger?: CompleteTaskTrigger;
	onTaskComplete?: (taskId: string) => Promise<void> | void;
}

interface TaskDescriptionCellContentProps extends TaskDescriptionCellProps {
	cloudId: string;
	userId: string | null;
	profilecardProvider?: ReturnType<typeof useProfilecardProvider>;
}

interface TaskValueCellProps {
	task: TaskEntity;
	column: Exclude<TasksReportColumn, 'description'>;
}

type MentionClickEvent = {
	metaKey?: boolean;
	ctrlKey?: boolean;
	button?: number;
	preventDefault?: () => void;
	stopPropagation?: () => void;
};

const styles = cssMap({
	cell: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		verticalAlign: 'top',
	},
	descriptionContent: {
		display: 'flex',
		gap: token('space.075'),
		alignItems: 'flex-start',
	},
	descriptionBody: {
		flexGrow: 1,
	},
	completionError: {
		marginTop: token('space.050'),
		color: token('color.text.danger'),
	},
	checkboxCell: {
		paddingTop: token('space.025'),
	},
	valueCellContent: {
		font: token('font.body'),
	},
});

const MISSING_COMPLETION_DATE = '--';

/** Format an ISO date string to a locale-aware short date, e.g. "Apr 22, 2026". */
function formatDate(iso: string | null, locale?: string): string {
	if (!iso) return '';
	try {
		return new Intl.DateTimeFormat(locale, {
			timeZone: 'UTC',
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		}).format(new Date(iso));
	} catch {
		return iso;
	}
}

function renderAdf(
	adf: string | null,
	dataProviders?: ProviderFactory,
	eventHandlers?: EventHandlers,
): React.ReactNode {
	if (!adf) return null;
	try {
		const doc = JSON.parse(adf);
		return (
			<RendererActionsContext>
				<ReactRenderer document={doc} dataProviders={dataProviders} eventHandlers={eventHandlers} />
			</RendererActionsContext>
		);
	} catch {
		return <span>{adf}</span>;
	}
}

function isModifiedMentionClick(event?: MentionClickEvent): boolean {
	return Boolean(event?.metaKey || event?.ctrlKey || event?.button === 1);
}

export const TaskRow = ({
	task,
	columns,
	taskUpdateTrigger,
	onTaskComplete,
}: TaskRowProps): React.JSX.Element => {
	return (
		<Box as="tr">
			{columns.map((column) => (
				<Box as="td" key={column} xcss={styles.cell}>
					{column === 'description' ? (
						<TaskDescriptionCell
							task={task}
							taskUpdateTrigger={taskUpdateTrigger}
							onTaskComplete={onTaskComplete}
						/>
					) : (
						<TaskValueCell task={task} column={column} />
					)}
				</Box>
			))}
		</Box>
	);
};

export const TaskDescriptionCell = ({
	task,
	taskUpdateTrigger,
	onTaskComplete,
}: TaskDescriptionCellProps): React.JSX.Element => {
	const { cloudId, userId } = useSessionData();

	if (
		fg('people-teams_migrate-user-profile-card') ||
		expValEquals('pt_user_profile_card_migration_exp', 'isEnabled', true)
	) {
		return (
			<TaskDescriptionCellWithProfileCard
				task={task}
				taskUpdateTrigger={taskUpdateTrigger}
				onTaskComplete={onTaskComplete}
				cloudId={cloudId}
				userId={userId}
			/>
		);
	}

	return (
		<TaskDescriptionCellContent
			task={task}
			taskUpdateTrigger={taskUpdateTrigger}
			onTaskComplete={onTaskComplete}
			cloudId={cloudId}
			userId={userId}
		/>
	);
};

const TaskDescriptionCellWithProfileCard = ({
	cloudId,
	userId,
	...props
}: Omit<TaskDescriptionCellContentProps, 'profilecardProvider'>): React.JSX.Element => {
	const { push } = useRouteActions();
	const profilecardProvider = useProfilecardProvider({ push, userId, cloudId });

	return (
		<TaskDescriptionCellContent
			{...props}
			cloudId={cloudId}
			userId={userId}
			profilecardProvider={profilecardProvider}
		/>
	);
};

const TaskDescriptionCellContent = ({
	task,
	taskUpdateTrigger,
	onTaskComplete,
	cloudId,
	userId,
	profilecardProvider,
}: TaskDescriptionCellContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { push } = useRouteActions();
	const {
		completeTask,
		loading,
		error: completeTaskError,
	} = useCompleteTask({
		trigger: taskUpdateTrigger,
	});
	const [isCompleting, setIsCompleting] = useState(false);
	const [completionError, setCompletionError] = useState<Error | null>(null);
	const isMounted = useRef(true);
	const displayedCompletionError = completionError ?? completeTaskError;
	const adfDataProviders = useMemo(() => {
		if (!cloudId || !userId) {
			return undefined;
		}

		const providerFactory = ProviderFactory.create({});
		providerFactory.setProvider(
			'mentionProvider',
			createMentionsConfig(cloudId, userId).then((config) => config.mentionResource),
		);
		if (profilecardProvider) {
			providerFactory.setProvider('profilecardProvider', profilecardProvider);
		}

		return providerFactory;
	}, [cloudId, userId, profilecardProvider]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		return () => {
			adfDataProviders?.destroy();
		};
	}, [adfDataProviders]);

	const adfEventHandlers = useMemo<EventHandlers | undefined>(
		() =>
			profilecardProvider
				? undefined
				: {
						mention: {
							onClick: (mentionedUserId, _text, event?: MentionClickEvent) => {
								const targetUrl = buildTaskAssigneeProfileUrl(mentionedUserId);
								event?.preventDefault?.();
								event?.stopPropagation?.();

								const shouldOpenInNewTab = isModifiedMentionClick(event);

								if (shouldOpenInNewTab) {
									if (typeof window !== 'undefined') {
										window.open(targetUrl, '_blank', 'noopener,noreferrer');
									}
									return;
								}

								push(targetUrl);
							},
						},
					},
		[profilecardProvider, push],
	);

	const handleCompleteTask = useCallback(async () => {
		if (!task.taskId || !task.container?.contentId || isCompleting || task.status === 'COMPLETE') {
			return;
		}

		setCompletionError(null);
		setIsCompleting(true);

		try {
			await completeTask(task.container.contentId, task.taskId);
			await onTaskComplete?.(task.id);
		} catch (error) {
			const completionErrorInstance =
				error instanceof Error ? error : new Error('Failed to complete task in tasks report row');

			if (isMounted.current) {
				setCompletionError(completionErrorInstance);
			}

			getMonitoringClient().submitError(completionErrorInstance, {
				attribution: Attribution.PAGE_EXPERIENCES,
			});
		} finally {
			if (isMounted.current) {
				setIsCompleting(false);
			}
		}
	}, [
		completeTask,
		isCompleting,
		onTaskComplete,
		task.container?.contentId,
		task.id,
		task.status,
		task.taskId,
	]);

	const handleCompleteTaskCheckboxChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (event.currentTarget.checked) {
				void handleCompleteTask();
			}
		},
		[handleCompleteTask],
	);

	return (
		<Box xcss={styles.descriptionContent}>
			<Box xcss={styles.checkboxCell}>
				<Checkbox
					isChecked={task.status === 'COMPLETE' || isCompleting}
					onChange={handleCompleteTaskCheckboxChange}
					isDisabled={task.status === 'COMPLETE' || isCompleting || loading}
					aria-label={formatMessage(i18n.completeTaskLabel)}
					testId={`task-complete-checkbox-${task.taskId}`}
				/>
			</Box>
			<Box xcss={styles.descriptionBody}>
				{renderAdf(task.adf, adfDataProviders, adfEventHandlers)}
				{displayedCompletionError ? (
					<Box xcss={styles.completionError} role="alert">
						{formatMessage(i18n.completeTaskError)}
					</Box>
				) : null}
			</Box>
		</Box>
	);
};

export const TaskValueCell = ({ task, column }: TaskValueCellProps): React.JSX.Element => {
	const { locale } = useIntl();
	const locationUrl = normalizeTaskUrl(task.container?.url);
	let content: React.ReactNode = '';

	switch (column) {
		case 'duedate':
			content = formatDate(task.dueAt, locale);
			break;

		case 'completedate':
			if (task.completedAt) {
				content = formatDate(task.completedAt, locale);
			} else if (task.status === 'COMPLETE') {
				content = MISSING_COMPLETION_DATE;
			}
			break;

		case 'assignee':
			content = task.assignedTo?.displayName ? (
				<Anchor href={buildTaskAssigneeProfileUrl(task.assignedTo.accountId)}>
					{task.assignedTo.displayName}
				</Anchor>
			) : (
				''
			);
			break;

		case 'location':
			content =
				locationUrl && task.container?.title ? (
					<Anchor href={locationUrl}>{task.container.title}</Anchor>
				) : (
					(task.container?.title ?? '')
				);
			break;

		case 'labels':
			content = (task.container?.labels ?? []).join(', ');
			break;

		default:
			break;
	}

	return <Box xcss={styles.valueCellContent}>{content}</Box>;
};
